import { Descriptions, Image, Modal, Skeleton, Tag, Typography } from "antd";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { StatusTag, type StatusTone } from "@/shared/components/StatusTag";
import { resolveImageUrl } from "@/shared/utils/resolveImageUrl";
import { useDailyAttendance } from "../hooks/useDailyAttendance";
import { useDailyAttendanceModalStore } from "../store/daily-attendance-modal.store";
import type { AttendanceStatus, DailyAttendance } from "../types";
import { EventBadges } from "./AttendanceTableCells";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Tashkent";

const STATUS_LABELS: Record<AttendanceStatus, string> = {
	PRESENT: "Kelgan",
	ABSENT: "Qatnashmagan",
	LATE: "Kech kelgan",
	LEFT_EARLY: "Erta ketgan",
	EXCUSED: "Sababli",
	PARTIAL: "Qisman",
};

const STATUS_COLORS: Record<AttendanceStatus, StatusTone> = {
	PRESENT: "success",
	ABSENT: "error",
	LATE: "warning",
	LEFT_EARLY: "warning",
	EXCUSED: "processing",
	PARTIAL: "warning",
};

const PERIOD_LABELS = {
	morning: "Ertalab",
	noon: "Kunduz",
	evening: "Kechqurun",
} as const;

function formatTime(iso: string | null | undefined): string {
	if (!iso) {
		return "—";
	}
	return dayjs.utc(iso).tz(TZ).format("HH:mm");
}

/** Legacy pre-cutover row: no period slots were ever populated — fall back to the
 *  old firstIn/lastOut display so historical records stay readable without a data migration.
 *  NOTE: returns a Fragment (not a component tag) — `Descriptions` flattens Fragment/array
 *  children into rows but does not unwrap custom component elements. */
function renderLegacyInOut(data: DailyAttendance) {
	return (
		<>
			<Descriptions.Item label="Kirish">
				<div className="flex items-center gap-2">
					<span className="font-mono">{formatTime(data.firstInTime)}</span>
					{data.firstInTerritoryName && <Tag>{data.firstInTerritoryName}</Tag>}
					<EventBadges
						display={{
							insideTerritory: data.firstInInsideTerritory ?? null,
							photoUrl: data.firstInPhotoUrl ?? null,
							noPhotoFlag: !data.firstInPhotoUrl,
							noGeofenceFlag: data.firstInInsideTerritory == null,
						}}
					/>
				</div>
			</Descriptions.Item>
			<Descriptions.Item label="Chiqish">
				<div className="flex items-center gap-2">
					<span className="font-mono">{formatTime(data.lastOutTime)}</span>
					{data.lastOutTerritoryName && <Tag>{data.lastOutTerritoryName}</Tag>}
					<EventBadges
						display={{
							insideTerritory: data.lastOutInsideTerritory ?? null,
							photoUrl: data.lastOutPhotoUrl ?? null,
							noPhotoFlag: !data.lastOutPhotoUrl,
							noGeofenceFlag: data.lastOutInsideTerritory == null,
						}}
					/>
				</div>
			</Descriptions.Item>
		</>
	);
}

/** Single period row (time + territory + badges + manual tag), or a placeholder when unfilled. */
function renderPeriodItem(
	period: "morning" | "noon" | "evening",
	slot: DailyAttendance["morning"]
) {
	return (
		<Descriptions.Item key={period} label={PERIOD_LABELS[period]}>
			{slot ? (
				<div className="flex items-center gap-2">
					<span className="font-mono">{formatTime(slot.time)}</span>
					{slot.territoryName && <Tag>{slot.territoryName}</Tag>}
					<EventBadges
						display={{
							insideTerritory: slot.insideTerritory,
							photoUrl: slot.photoUrl,
							noPhotoFlag: !slot.photoUrl,
							noGeofenceFlag: slot.insideTerritory == null,
						}}
					/>
					{slot.isManual && <Tag color="blue">Qo'lda kiritilgan</Tag>}
				</div>
			) : (
				<span className="text-ink-faint">—</span>
			)}
		</Descriptions.Item>
	);
}

/** 3x-daily period sections (Ertalab/Kunduz/Kechqurun). */
function renderPeriodSections(data: DailyAttendance) {
	return (["morning", "noon", "evening"] as const).map((period) =>
		renderPeriodItem(period, data[period])
	);
}

/** Override reasons from any filled period slot (not just one hardcoded slot). */
function renderOverrideReasons(data: DailyAttendance) {
	return (["morning", "noon", "evening"] as const)
		.filter((period) => data[period]?.overrideReason)
		.map((period) => (
			<Descriptions.Item key={`reason-${period}`} label="Sabab">
				{data[period]?.overrideReason}
			</Descriptions.Item>
		));
}

export function AttendanceDetailModal() {
	const { detailModalOpen, selectedId, closeDetailModal } = useDailyAttendanceModalStore();
	const { data, isLoading } = useDailyAttendance(selectedId ?? "");

	return (
		<Modal
			title="Davomat tafsilotlari"
			open={detailModalOpen}
			onCancel={closeDetailModal}
			footer={null}
			destroyOnHidden
		>
			{isLoading || !data ? (
				<Skeleton active paragraph={{ rows: 6 }} />
			) : (
				<div className="space-y-4">
					<Descriptions column={1} bordered size="small">
						<Descriptions.Item label="Xodim">{data.staff?.fullname ?? "—"}</Descriptions.Item>
						<Descriptions.Item label="Sana">
							{dayjs(data.date).format("DD.MM.YYYY")}
						</Descriptions.Item>
						<Descriptions.Item label="Holat">
							<StatusTag tone={STATUS_COLORS[data.status] ?? "default"}>
								{STATUS_LABELS[data.status] ?? data.status}
							</StatusTag>
						</Descriptions.Item>
						{data.morning == null && data.noon == null && data.evening == null
							? renderLegacyInOut(data)
							: renderPeriodSections(data)}
						{renderOverrideReasons(data)}
					</Descriptions>
					{data.photoReportFileUrl && (
						<div>
							<Typography.Text type="secondary" className="block mb-2">
								Foto-hisobot
							</Typography.Text>
							<Image
								width={160}
								height={160}
								src={resolveImageUrl(data.photoReportFileUrl)}
								alt="Foto hisobot"
								style={{ objectFit: "cover", borderRadius: 6 }}
							/>
						</div>
					)}
				</div>
			)}
		</Modal>
	);
}
